/**
 * Czas pracy — lokalizacja A4.
 *
 * ZASADA NADRZĘDNA: wszystkie godziny liczymy i prezentujemy w strefie Europe/Warsaw,
 * nigdy w czasie lokalnym przeglądarki. Na hali są komputery z rozjechanymi zegarami
 * i widok raportu nie może zależeć od ustawień stacji.
 */

export const TZ = 'Europe/Warsaw';

/** Zmiana: 6:00-14:00. Minuty liczone od północy. */
export const SHIFT_START_MIN = 6 * 60;   // 360
export const SHIFT_END_MIN = 14 * 60;    // 840

/** Regulaminowe przerwy. Kolejność = kolejność na ekranie. */
export const BREAKS = [
  { key: 'br_meeting', label: 'Spotkanie 6:00-6:05', minutes: 5,  hint: '6:00' },
  { key: 'br_5a',      label: 'Przerwa 5 min',       minutes: 5,  hint: null },
  { key: 'br_20',      label: 'Przerwa 20 min',      minutes: 20, hint: null },
  { key: 'br_5b',      label: 'Przerwa 5 min',       minutes: 5,  hint: null },
  { key: 'br_5s',      label: '5S',                  minutes: 5,  hint: 'koniec zmiany' },
] as const;

/** Dodatkowa przerwa należna, gdy pracownik zostaje w nadgodzinach. */
export const OVERTIME_BREAK_MIN = 15;

/** Suma przerw regulaminowych: 40 min. */
export const TOTAL_BREAK_MIN = BREAKS.reduce((s, b) => s + b.minutes, 0);

/** Nominalny dzień pracy: 8h - 40 min przerw = 440 min = 7,33 h. */
export const NOMINAL_DAY_MIN = SHIFT_END_MIN - SHIFT_START_MIN - TOTAL_BREAK_MIN;  // 440

export type BreakKey = (typeof BREAKS)[number]['key'];
export type BreakFlags = Partial<Record<BreakKey | 'br_overtime', boolean | number>>;

/**
 * Excel przyjmował godziny jako liczby bez dwukropka (600 = 6:00, 1400 = 14:00),
 * bo pracownicy mylili się przy dwukropku. Aplikacja daje pickery czasu,
 * ale parser zostaje — do importu historii i dla kogoś, kto woli wklepać "630".
 */
export function parseTimeToMinutes(input: string | number | null | undefined): number | null {
  if (input === null || input === undefined || input === '') return null;
  const s = String(input).trim();

  const withColon = s.match(/^(\d{1,2})[:.](\d{2})$/);
  if (withColon) {
    const h = Number(withColon[1]), m = Number(withColon[2]);
    return h <= 23 && m <= 59 ? h * 60 + m : null;
  }
  if (!/^\d{1,4}$/.test(s)) return null;
  const n = Number(s);
  if (n < 24) return n * 60;                 // "6" = 6:00, jak w Excelu
  const h = Math.trunc(n / 100), m = n % 100;
  return h <= 23 && m <= 59 ? h * 60 + m : null;
}

export function formatMinutes(min: number | null | undefined): string {
  if (min === null || min === undefined) return '';
  const norm = ((min % 1440) + 1440) % 1440;
  return `${String(Math.trunc(norm / 60)).padStart(2, '0')}:${String(norm % 60).padStart(2, '0')}`;
}

/** Czas trwania jako "7h 20m" — do raportów. */
export function formatDuration(min: number): string {
  const sign = min < 0 ? '-' : '';
  const a = Math.abs(Math.round(min));
  const h = Math.trunc(a / 60), m = a % 60;
  return h === 0 ? `${sign}${m}m` : m === 0 ? `${sign}${h}h` : `${sign}${h}h ${m}m`;
}

/**
 * Godziny dziesiętne z przecinkiem, np. "7,33 h".
 *
 * Excel liczy w godzinach dziesiętnych i tak myśli o czasie cała załoga:
 * norma to "0,9033 h", a dniówka to "7,33". Zapis "7h 20m" jest czytelniejszy
 * przy planowaniu dnia, ale bez wersji dziesiętnej obok każdy i tak przelicza
 * w głowie — a "7,33" bywa mylone z 7 godzinami i 33 minutami.
 */
export function formatDecimalHours(min: number, miejsc = 2): string {
  return `${(min / 60).toFixed(miejsc).replace('.', ',')} h`;
}

/** Suma odhaczonych przerw w minutach. */
export function breakMinutes(flags: BreakFlags): number {
  let total = 0;
  for (const b of BREAKS) if (flags[b.key]) total += b.minutes;
  if (flags.br_overtime) total += OVERTIME_BREAK_MIN;
  return total;
}

export interface EntryTimes {
  start_min: number;
  end_min: number;
}

/** Czas brutto z obsługą przejścia przez północ (jak LET() w Excelu). */
export function grossMinutes({ start_min, end_min }: EntryTimes): number {
  return end_min < start_min ? end_min + 1440 - start_min : end_min - start_min;
}

/** Czas netto: brutto minus odhaczone przerwy. To jest "ilość godzin" z Excela. */
export function netMinutes(entry: EntryTimes & BreakFlags): number {
  return Math.max(0, grossMinutes(entry) - breakMinutes(entry));
}

/**
 * Blok czasu wpisu, z rozwinięciem przejścia przez północ.
 * 22:00-06:00 to nie jest blok ujemny, tylko 1320-1800.
 */
export function blokCzasu({ start_min, end_min }: EntryTimes): { od: number; do: number } {
  return { od: start_min, do: end_min < start_min ? end_min + 1440 : end_min };
}

/**
 * Czy dwa bloki czasu tego samego człowieka na siebie nachodzą.
 *
 * Bez tej reguły ten sam pracownik mógł rozpisać 06:00-10:00, 06:00-14:00
 * i 10:00-18:00 tego samego dnia — 18 godzin pracy w ośmiogodzinnej zmianie.
 * Norma jednostki pracy jest wprawdzie dzielona i nie da się jej przekroczyć,
 * więc własnej efektywności to nie zawyża, ale psuje wszystko dookoła:
 * czas rzeczywisty operacji rośnie, przez co analiza norm uznaje dobrą normę
 * za za ciasną, a przy wspólnej sztuce nachodzące wpisy zabierają część normy
 * koledze, który rozpisał się uczciwie.
 *
 * Styk nie jest nachodzeniem: wpis kończący się o 10:00 i zaczynający o 10:00
 * to normalna kolej rzeczy.
 */
export function nachodza(a: EntryTimes, b: EntryTimes): boolean {
  const x = blokCzasu(a);
  const y = blokCzasu(b);
  return x.od < y.do && y.od < x.do;
}

/**
 * Czas dostępny pracownika w danym dniu = nominał + nadgodziny.
 * Nadgodziny liczymy od końca zmiany do najpóźniejszego zarejestrowanego "stop",
 * pomniejszone o dodatkową 15-minutową przerwę, która się wtedy należy.
 * To jest MIANOWNIK efektywności — czas niezarejestrowany działa na niekorzyść,
 * bo pracownik fizycznie był w pracy.
 */
export function availableMinutes(latestEndMin: number | null): number {
  if (latestEndMin === null || latestEndMin <= SHIFT_END_MIN) return NOMINAL_DAY_MIN;
  const overtime = latestEndMin - SHIFT_END_MIN;
  return NOMINAL_DAY_MIN + Math.max(0, overtime - OVERTIME_BREAK_MIN);
}

/** Dzisiejsza data (YYYY-MM-DD) w strefie zakładu, niezależnie od zegara stacji. */
export function todayInPlant(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: TZ }).format(now);  // sv-SE => YYYY-MM-DD
}

/** Aktualna godzina zakładowa jako minuty od północy — do znacznika "dane na HH:MM". */
export function nowMinutesInPlant(now: Date = new Date()): number {
  const p = new Intl.DateTimeFormat('en-GB', {
    timeZone: TZ, hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(now);
  const h = Number(p.find((x) => x.type === 'hour')?.value ?? 0);
  const m = Number(p.find((x) => x.type === 'minute')?.value ?? 0);
  return h * 60 + m;
}

/** Numer tygodnia ISO — zastępuje WEEKNUM() z Excela w raportach tygodniowych. */
export function isoWeek(date: string): { year: number; week: number } {
  const d = new Date(`${date}T00:00:00Z`);
  const day = (d.getUTCDay() + 6) % 7;              // pon = 0
  d.setUTCDate(d.getUTCDate() - day + 3);           // czwartek tego tygodnia
  const year = d.getUTCFullYear();
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const week = 1 + Math.round(((d.getTime() - jan4.getTime()) / 86400000 - 3 + ((jan4.getUTCDay() + 6) % 7)) / 7);
  return { year, week };
}

export const MONTHS_PL = [
  'styczeń', 'luty', 'marzec', 'kwiecień', 'maj', 'czerwiec',
  'lipiec', 'sierpień', 'wrzesień', 'październik', 'listopad', 'grudzień',
] as const;

export function monthPl(date: string): string {
  return MONTHS_PL[Number(date.slice(5, 7)) - 1] ?? '';
}
